const NEAR_BOTTOM_VIEWPORT_RATIO = 0.1;
const PIN_SETTLE_FRAMES = 30;
const CHAT_V2_INPUT_SELECTOR = '.raid-control-mobile-chat-v2-composer textarea';
const CHAT_INPUT_SELECTOR = [
  '.raid-control-room.mobile .raid-control-composer textarea',
  CHAT_V2_INPUT_SELECTOR,
].join(',');

export function createMobileChatMessageScrollKeeper() {
  let messageList = null;
  let resizeObserver = null;
  let mutationObserver = null;
  let restoreFrame = 0;
  let pinnedSettleFrame = 0;
  let pinnedSettleFramesRemaining = 0;
  let previousOverflowAnchor = '';
  let nearBottom = true;
  let bottomGap = 0;
  let scrollTopAnchor = 0;
  let latestPinned = false;

  const clearRestoreFrame = () => {
    if (restoreFrame) window.cancelAnimationFrame(restoreFrame);
    restoreFrame = 0;
  };

  const clearPinnedSettleFrame = () => {
    if (pinnedSettleFrame) window.cancelAnimationFrame(pinnedSettleFrame);
    pinnedSettleFrame = 0;
    pinnedSettleFramesRemaining = 0;
  };

  const queuePinnedSettle = () => {
    if (!messageList || !latestPinned || pinnedSettleFrame) return;
    pinnedSettleFrame = window.requestAnimationFrame(() => {
      pinnedSettleFrame = 0;
      if (!messageList || !latestPinned || messageList.dataset.history === 'true') return;
      messageList.scrollTop = messageList.scrollHeight;
      scrollTopAnchor = messageList.scrollTop;
      nearBottom = true;
      bottomGap = 0;
      if (pinnedSettleFramesRemaining > 0) {
        pinnedSettleFramesRemaining -= 1;
        queuePinnedSettle();
      }
    });
  };

  const capture = () => {
    if (!messageList || messageList.dataset.history === 'true') return;
    if (latestPinned) {
      nearBottom = true;
      bottomGap = 0;
      scrollTopAnchor = messageList.scrollTop;
      queuePinnedSettle();
      return;
    }
    scrollTopAnchor = messageList.scrollTop;
    bottomGap = Math.max(
      messageList.scrollHeight - messageList.scrollTop - messageList.clientHeight,
      0,
    );
    const nearBottomRange = Math.max(messageList.clientHeight * NEAR_BOTTOM_VIEWPORT_RATIO, 1);
    nearBottom = bottomGap <= nearBottomRange;
  };

  const restore = () => {
    restoreFrame = 0;
    if (!messageList || messageList.dataset.history === 'true') return;
    if (latestPinned || nearBottom) {
      messageList.scrollTop = messageList.scrollHeight;
      scrollTopAnchor = messageList.scrollTop;
      bottomGap = 0;
      if (latestPinned) queuePinnedSettle();
      return;
    }
    const maxScrollTop = Math.max(messageList.scrollHeight - messageList.clientHeight, 0);
    messageList.scrollTop = Math.min(scrollTopAnchor, maxScrollTop);
  };

  const queueRestore = () => {
    if (!messageList || restoreFrame) return;
    restoreFrame = window.requestAnimationFrame(restore);
  };

  const scrollToLatest = () => {
    if (!messageList) return;
    clearRestoreFrame();
    nearBottom = true;
    bottomGap = 0;
    messageList.scrollTop = messageList.scrollHeight;
    scrollTopAnchor = messageList.scrollTop;
    queueRestore();
  };

  const pinLatest = () => {
    latestPinned = true;
    pinnedSettleFramesRemaining = PIN_SETTLE_FRAMES;
    scrollToLatest();
    queuePinnedSettle();
  };

  const releaseLatest = () => {
    if (!latestPinned) return;
    scrollToLatest();
    latestPinned = false;
    clearPinnedSettleFrame();
    nearBottom = true;
    bottomGap = 0;
    scrollTopAnchor = messageList?.scrollTop || 0;
  };

  const isChatInputEvent = (event) => event.target instanceof Element
    && event.target.matches(CHAT_INPUT_SELECTOR)
    && Boolean(messageList?.closest('.raid-control-workspace.mobile.view-chat, .raid-control-mobile-chat-v2'));

  const isV2InputEvent = (event) => event.target instanceof Element
    && event.target.matches(CHAT_V2_INPUT_SELECTOR);

  const handleInputFocusStart = (event) => {
    if (!isChatInputEvent(event)) return;
    capture();
    if (!isV2InputEvent(event) && nearBottom) pinLatest();
  };

  const handlePointerDown = (event) => {
    handleInputFocusStart(event);
  };

  const handleFocusIn = (event) => {
    handleInputFocusStart(event);
  };

  const handleFocusOut = (event) => {
    if (!(event.target instanceof Element) || !event.target.matches(CHAT_INPUT_SELECTOR)) return;
    releaseLatest();
    capture();
  };

  const detach = () => {
    clearRestoreFrame();
    clearPinnedSettleFrame();
    if (messageList) {
      messageList.removeEventListener('scroll', capture);
      if (previousOverflowAnchor) messageList.style.setProperty('overflow-anchor', previousOverflowAnchor);
      else messageList.style.removeProperty('overflow-anchor');
    }
    document.removeEventListener('pointerdown', handlePointerDown, true);
    document.removeEventListener('focusin', handleFocusIn, true);
    document.removeEventListener('focusout', handleFocusOut, true);
    resizeObserver?.disconnect();
    mutationObserver?.disconnect();
    resizeObserver = null;
    mutationObserver = null;
    messageList = null;
    previousOverflowAnchor = '';
    nearBottom = true;
    bottomGap = 0;
    scrollTopAnchor = 0;
    latestPinned = false;
  };

  const attach = (nextMessageList) => {
    if (nextMessageList === messageList) return;
    detach();
    messageList = nextMessageList;
    if (!messageList) return;

    previousOverflowAnchor = messageList.style.getPropertyValue('overflow-anchor');
    messageList.style.setProperty('overflow-anchor', 'none');
    nearBottom = true;
    bottomGap = 0;
    scrollTopAnchor = messageList.scrollTop;
    latestPinned = false;
    messageList.addEventListener('scroll', capture, { passive: true });
    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('focusin', handleFocusIn, true);
    document.addEventListener('focusout', handleFocusOut, true);
    resizeObserver = typeof ResizeObserver === 'function'
      ? new ResizeObserver(queueRestore)
      : null;
    resizeObserver?.observe(messageList);
    mutationObserver = new MutationObserver(queueRestore);
    mutationObserver.observe(messageList, { childList: true });
    queueRestore();
  };

  return {
    attach,
    capture,
    queueRestore,
    scrollToLatest,
    pinLatest,
    releaseLatest,
    destroy: detach,
  };
}
